// The position list is a FLAT pre-order array in which `depth` is the only
// structural truth (see working-copy.ts for the invariants). These helpers are
// the single place that reads that structure — nothing else may infer parents
// or subtrees by hand.

/** Minimum a row must expose to take part in the tree math. */
export interface DepthRow {
  depth: number;
}

/**
 * Index one past the last descendant of `index` — the subtree is the half-open
 * range `[index, subtreeEnd(index))`. A row without children yields `index + 1`.
 */
export function subtreeEnd(rows: readonly DepthRow[], index: number): number {
  const depth = rows[index]?.depth ?? 0;
  let end = index + 1;
  while (end < rows.length && rows[end].depth > depth) end++;
  return end;
}

/** Index of the nearest preceding row one level up; -1 for a root row. */
export function parentIndex(rows: readonly DepthRow[], index: number): number {
  const depth = rows[index]?.depth ?? 0;
  if (depth === 0) return -1;
  for (let i = index - 1; i >= 0; i--) if (rows[i].depth < depth) return i;
  return -1;
}

/** Direct children only — not the whole subtree. */
export function childCount(rows: readonly DepthRow[], index: number): number {
  const depth = rows[index]?.depth ?? 0;
  let count = 0;
  for (let i = index + 1; i < subtreeEnd(rows, index); i++) {
    if (rows[i].depth === depth + 1) count++;
  }
  return count;
}

/** Indenting is only legal under an existing previous sibling. */
export function hasPreviousSibling(rows: readonly DepthRow[], index: number): boolean {
  const depth = rows[index]?.depth ?? 0;
  for (let i = index - 1; i >= 0; i--) {
    if (rows[i].depth === depth) return true;
    if (rows[i].depth < depth) return false;
  }
  return false;
}

/**
 * Grows a set of picked indexes to the full subtrees they head, so a structural
 * operation can never tear a parent away from its children.
 */
export function expandToSubtrees(
  rows: readonly DepthRow[],
  indexes: Iterable<number>
): number[] {
  const expanded = new Set<number>();
  for (const index of indexes) {
    for (let i = index; i < subtreeEnd(rows, index); i++) expanded.add(i);
  }
  return [...expanded].sort((a, b) => a - b);
}

/**
 * Repaired depth per row: the first row is a root and none descends more than
 * one level below its predecessor. Returns the DEPTHS, not new rows — the
 * drafts must keep their object identity, which the row selection and the
 * product-match map are both keyed by.
 */
export function normalizeDepths(rows: readonly DepthRow[]): number[] {
  let previous = -1;
  return rows.map((row) => {
    const depth = Math.max(0, Math.min(row.depth, previous + 1));
    previous = depth;
    return depth;
  });
}

/**
 * Position number for display: the document's own `item_number` wins; a row
 * without one is numbered by its ordinal among its siblings, prefixed with the
 * parent's number ("2.3"). Numbers are never written back to the drafts —
 * an extracted `item_number` is document truth and stays untouched.
 */
export function displayNumber(
  rows: readonly (DepthRow & { fields: { item_number: string } })[],
  index: number
): string {
  const own = rows[index]?.fields.item_number.trim();
  if (own) return own;
  const depth = rows[index]?.depth ?? 0;
  let ordinal = 1;
  for (let i = index - 1; i >= 0; i--) {
    if (rows[i].depth < depth) break;
    if (rows[i].depth === depth) ordinal++;
  }
  const parent = parentIndex(rows, index);
  return parent < 0 ? String(ordinal) : `${displayNumber(rows, parent)}.${ordinal}`;
}

/**
 * Rebuilds the tree the flat list encodes. The plausibility endpoint sums only
 * the DIRECT children of a node and recurses into intermediate ones — handing
 * it the flat list would count every sub-position twice.
 */
export function nestPositions<TRow extends DepthRow, TOut extends { items?: TOut[] }>(
  rows: readonly TRow[],
  project: (row: TRow, index: number) => TOut
): TOut[] {
  const roots: TOut[] = [];
  // Stack of the open ancestors, indexed by depth.
  const open: TOut[] = [];
  rows.forEach((row, index) => {
    const node = project(row, index);
    const depth = Math.max(0, Math.min(row.depth, open.length));
    if (depth === 0) roots.push(node);
    else {
      const parent = open[depth - 1];
      (parent.items ??= []).push(node);
    }
    open.length = depth;
    open.push(node);
  });
  return roots;
}
